import React, { useState } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { apiClient } from '../utils/apiUtils';

const ChangePasswordModal = ({ show, onHide, userEmail }) => {
  const [step, setStep] = useState(1); // 1: Request code, 2: Enter code, 3: Change password
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleClose = () => {
    setStep(1);
    setError('');
    setSuccess('');
    setVerificationCode('');
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    onHide();
  };

  const handleRequestCode = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    console.log('🔍 Requesting verification code for:', userEmail);
    console.log('🔍 API Base URL:', apiClient.defaults.baseURL);

    try {
      const response = await apiClient.post('/auth/request-password-change', {
        email: userEmail
      }, {
        timeout: 120000 // 120 seconds timeout - Render free tier can take up to 2 minutes to wake up
      });
      
      console.log('✅ Verification code sent successfully');
      
      setSuccess('Verification code has been sent to your email. Please check your inbox and spam folder.');
      setStep(2);
    } catch (error) {
      console.error('❌ Verification code request failed:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response,
        request: error.request,
        status: error.response?.status,
        data: error.response?.data
      });

      let errorMessage = 'Failed to send verification code';
      
      if (error.code === 'ECONNABORTED') {
        // Timeout error
        errorMessage = 'Request timed out. The server may be waking up from sleep (this can take up to 2 minutes on Render free tier). Please wait 1-2 minutes and try again.';
        console.error('❌ Request timed out after 60 seconds');
      } else if (error.response) {
        // Server responded with error
        errorMessage = error.response.data?.error || error.response.data?.message || `Server error (${error.response.status})`;
        console.error('❌ Server error response:', error.response.status, error.response.data);
      } else if (error.request) {
        // Request made but no response received
        errorMessage = 'Cannot reach the server. The backend may be starting up (first request after idle can take 1-2 minutes). Please wait and try again.';
        console.error('❌ No response from server - possible cold start or network issue');
      } else {
        // Something else happened
        errorMessage = `Request failed: ${error.message}`;
        console.error('❌ Unexpected error:', error.message);
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await apiClient.post('/auth/verify-password-code', {
        email: userEmail,
        code: verificationCode
      });
      setSuccess('Code verified successfully!');
      setStep(3);
    } catch (error) {
      setError(error.response?.data?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient.post('/auth/change-password', {
        email: userEmail,
        code: verificationCode,
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });
      
      if (response.data.success) {
        setSuccess('🎉 Password changed successfully! You will be logged out for security. Please log in again with your new password.');
        
        // Log out user after successful password change
        setTimeout(() => {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }, 3000); // Wait 3 seconds to show success message
      } else {
        setSuccess('Password changed successfully!');
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      centered 
      size="md"
      style={{ zIndex: 10100 }}
      backdrop="static"
      keyboard={false}
      backdropClassName="modal-backdrop"
    >
      <Modal.Header closeButton style={{ zIndex: 10120, position: 'relative', backgroundColor: '#fff' }}>
        <Modal.Title>
          <i className="bi bi-shield-lock me-2"></i>
          Change Password
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ position: 'relative', zIndex: 10120, backgroundColor: '#fff' }}>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')} className="d-flex align-items-center animate__animated animate__fadeIn">
            <i className="bi bi-check-circle-fill me-2 fs-5 text-success"></i>
            <div>
              <strong className="text-success">Success!</strong>
              <div className="small mt-1">{success}</div>
            </div>
          </Alert>
        )}

        {/* Step 1: Request Verification Code */}
        {step === 1 && (
          <div className="text-center">
            <div className="mb-4">
              <i className="bi bi-envelope-check text-primary" style={{ fontSize: '3rem' }}></i>
            </div>
            <h5>Verify Your Identity</h5>
            <p className="text-muted">
              We'll send a verification code to your registered email address:
              <br />
              <strong>{userEmail}</strong>
            </p>
            <Button 
              variant="primary" 
              onClick={handleRequestCode}
              disabled={loading}
              className="w-100"
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Sending Code...
                </>
              ) : (
                'Send Verification Code'
              )}
            </Button>
          </div>
        )}

        {/* Step 2: Enter Verification Code */}
        {step === 2 && (
          <div>
            <div className="text-center mb-4">
              <i className="bi bi-key text-primary" style={{ fontSize: '2.5rem' }}></i>
            </div>
            <h5>Enter Verification Code</h5>
            <p className="text-muted">
              Please enter the 6-digit code sent to your email.
            </p>
            <Form.Group className="mb-3">
              <Form.Label>Verification Code</Form.Label>
              <Form.Control
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                maxLength="6"
                className="text-center"
                style={{ fontSize: '1.2rem', letterSpacing: '0.2em' }}
              />
            </Form.Group>
            <div className="d-flex gap-2">
              <Button 
                variant="outline-secondary" 
                onClick={() => setStep(1)}
                className="flex-fill"
              >
                Back
              </Button>
              <Button 
                variant="primary" 
                onClick={handleVerifyCode}
                disabled={loading || verificationCode.length !== 6}
                className="flex-fill"
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Verifying...
                  </>
                ) : (
                  'Verify Code'
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Change Password */}
        {step === 3 && (
          <div>
            <div className="text-center mb-4">
              <i className="bi bi-lock text-success" style={{ fontSize: '2.5rem' }}></i>
            </div>
            <h5>Set New Password</h5>
            <p className="text-muted">
              Please enter your current password and choose a new password.
            </p>
            <Form onSubmit={handleChangePassword}>
              <Form.Group className="mb-3">
                <Form.Label>Current Password</Form.Label>
                <Form.Control
                  type="password"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  placeholder="Enter current password"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <Form.Control
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  placeholder="Enter new password"
                  minLength="6"
                  required
                />
                <Form.Text className="text-muted">
                  Password must be at least 6 characters long
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Confirm New Password</Form.Label>
                <Form.Control
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm new password"
                  minLength="6"
                  required
                />
              </Form.Group>

              <div className="d-flex gap-2">
                <Button 
                  variant="outline-secondary" 
                  onClick={() => setStep(2)}
                  className="flex-fill"
                >
                  Back
                </Button>
                <Button 
                  variant="success" 
                  type="submit"
                  disabled={loading}
                  className="flex-fill"
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Changing...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </Button>
              </div>
            </Form>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ChangePasswordModal;
